import { cva, type VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

/**
 * The one recipe every form control wears: input, select, textarea, and the
 * controls that are still written by hand.
 *
 * The default size is the dense one on purpose. `TextField` fixed its height at
 * `h-10` and ended up in 13 files while 179 controls were spelled out by hand at
 * `h-8` — a primitive that does not fit the screen is not used, so the call site
 * asks for the roomier size, never for the smaller one.
 *
 * ── A BORDA EM REPOUSO É A FORTE (09/09/2026) ─────────────────────────────
 *
 * Em repouso um campo desenhava `border-border` sobre `bg-surface`: uma caixa
 * branca com um contorno de 1,26:1 numa página quase branca. Um campo PREENCHIDO
 * ainda se lia pelo texto; um campo VAZIO não existia — a pessoa não via onde
 * escrever até passar o rato por cima, que é quando a borda finalmente aparecia.
 * O mesmo defeito no escuro (1,27:1), e o mesmo do `variant="secondary"` do
 * botão, corrigido no mesmo dia pelo mesmo motivo.
 *
 * `border-strong` existe para isto e o `tokens.css` escreve-o: é o que um
 * CONTROLO mostra, e tem de limpar 3:1 contra toda superfície em que possa
 * assentar (WCAG 1.4.11). Agora 3,45:1 sobre o fundo do campo no claro e 3,49:1
 * no escuro.
 *
 * E O HOVER TEVE DE SUBIR UM DEGRAU. Ele era `border-strong` — que passou a ser
 * o repouso, e um hover igual ao repouso é um hover que não existe. Passa a ser
 * `fg-muted`, o degrau seguinte da mesma escada (7,81:1 no claro, 6,38:1 no
 * escuro), então os três estados continuam a ser três: parado, apontado, focado.
 * O foco continua a ser o accent, que é a cor de "é aqui que escreves".
 *
 * ── PADRÃO CLAUDE (dono, 25/10/2026) ──────────────────────────────────────
 *
 * O campo passa a FLUTUAR: `rounded-xl`, `bg-surface-raised` e `shadow-sm`
 * sobre a página `surface`. A borda em repouso CONTINUA a forte — branco sobre
 * `#fafaf8` é 1,04:1, e a regra de 09/09 vale igual: o campo vazio tem de se
 * ver. O hover sobe a `fg-subtle`; o foco ganha o accent e um anel suave.
 */
export const control = cva(
	"w-full rounded-xl border border-border-strong bg-surface-raised text-fg shadow-sm placeholder:text-fg-subtle transition-[color,border-color,box-shadow] hover:border-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:opacity-50",
	{
		variants: {
			size: {
				xs: "h-7 px-2 text-xs",
				sm: "h-8 px-2.5 text-sm",
				md: "h-9 px-3 text-sm",
			},
		},
		defaultVariants: { size: "sm" },
	},
);

export type ControlSize = NonNullable<VariantProps<typeof control>["size"]>;

/**
 * The recipe first, the call site last, so an override wins instead of piling a
 * second value of the same property onto the element — the width of a control in
 * a toolbar, or the `h-auto py-1.5` a textarea needs in place of a fixed height.
 */
export function controlClass(size?: ControlSize, extra?: string): string {
	return twMerge(control({ size }), extra);
}
